import { useState, useEffect } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { BsInfoCircle } from "react-icons/bs";
import { fetchUsersList, reactivateUsers, deactivateUsers } from "../../api/endpoint";
import { saveUsersData } from "../../store/slice/usersSlice";
import Table from "../../components/table";
import Loader from "../../components/loader";
import Input from "../../components/input";
import Button from "../../components/button";
import AddUserModal from "./addUserModal";
import RemoveUserModal from "./removeUserModal";
import EditUserModal from "./editUserModal";
import UserDetailsModal from "./userDetailsModal";

const UserList = () => {
  const dispatch = useDispatch();
  const { auth } = useSelector((state) => state.login);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    name: "",
    startDate: "",
    endDate: "",
    isSubscribed: "",
    email: "",
    gender: "",
    latitude: "",
    longitude: "",
    address: "",
  });

  const [isDropdownFilterOpen, setIsDropdownFilterOpen] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modals
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(() => {
    if (auth) {
      fetchUsers(filters);
    }
  }, [auth, currentPage]);

  const fetchUsers = async (filters) => {
    try {
      setLoading(true);
      const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(
          ([_, value]) => value !== "" && value !== null && value !== undefined
        )
      );
      const params = {
        ...cleanedFilters,
        page: currentPage,
        size: pageSize,
      };

      const response = await axios.get(fetchUsersList, {
        headers: { Authorization: `Bearer ${auth}` },
        params,
      });

      const { content = [], totalElements, totalPages } = response?.data || {};

      const userData = content.map((user) => ({
        id: user.id,
        name: user.name,
        email: user.email,
        mobNo: user.mobileNumber,
        actions: () => getActionButtons(user.id, !user.suspended),
      }));

      dispatch(saveUsersData(content));
      setUsers(userData);
      setTotalElements(totalElements || 0);
      setTotalPages(totalPages || 0);
    } catch (error) {
      toast.error("Failed to fetch users");
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const getActionButtons = (userId, status) => (
    <div className="d-flex align-items-center">
      <div className="form-switch d-inline-block me-2">
        <input
          className="form-check-input"
          type="checkbox"
          role="button"
          checked={status}
          onChange={() => changeUserStatus(userId, status)}
          title="Toggle Active Status"
        />
      </div>
      <button
        className="btn p-0 mb-1"
        onClick={() => {
          setSelectedUserId(userId);
          setIsViewOpen(true);
        }}
        style={{ cursor: "pointer" }}
        title="View Details"
      >
        <BsInfoCircle size={20} />
      </button>
    </div>
  );

  const changeUserStatus = async (userId, status) => {
    try {
      setLoading(true);
      const url = status ? deactivateUsers : reactivateUsers;
      await axios.post(
        url,
        { authIds: [userId] },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      toast.success(`User successfully ${status ? "deactivated" : "reactivated"}`);
      fetchUsers(filters);
    } catch (error) {
      console.error("Error updating user status", error);
      toast.error("Error updating user status");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchUsers(filters);
    setIsDropdownFilterOpen(false);
  };

  const clearFilters = (e) => {
    e.preventDefault();
    const cleared = {
      name: "",
      startDate: "",
      endDate: "",
      isSubscribed: "",
      email: "",
      gender: "",
      latitude: "",
      longitude: "",
      address: "",
    };
    setFilters(cleared);
    setCurrentPage(0);
    fetchUsers(cleared);
    setIsDropdownFilterOpen(false);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 0 && pageNumber < totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const headings = [
    { name: "Name", fieldName: "name", isSortable: true, sortKey: "none" },
    { name: "Email", fieldName: "email", isSortable: false, sortKey: "none" },
    { name: "Mobile Number", fieldName: "mobNo", isSortable: false, sortKey: "none" },
    { name: "Actions", fieldName: "actions", isSortable: false, sortKey: "none" },
  ];

  const filtersDropdown = () => (
    <form style={{ minWidth: "20rem" }}>
      {/* Name + Subscribed */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="mb-1">Name:</label>
          <Input
            type="text"
            placeholder="Enter Name"
            value={filters?.name}
            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
          />
        </div>
        <div className="col-md-6">
          <label className="mb-1">Subscribed:</label>
          <Input
            type="select"
            value={filters?.isSubscribed}
            options={[
              { label: "Select", value: "" },
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
            onChange={(e) =>
              setFilters({ ...filters, isSubscribed: e.target.value })
            }
          />
        </div>
      </div>

      {/* Dates */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="mb-1">Start Date:</label>
          <Input
            type="date"
            value={filters?.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
        </div>
        <div className="col-md-6">
          <label className="mb-1">End Date:</label>
          <Input
            type="date"
            value={filters?.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
        </div>
      </div>

      {/* Email + Gender */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="mb-1">Email:</label>
          <Input
            type="email"
            placeholder="Enter Email"
            value={filters?.email}
            onChange={(e) => setFilters({ ...filters, email: e.target.value })}
          />
        </div>
        <div className="col-md-6">
          <label className="mb-1">Gender:</label>
          <Input
            type="select"
            value={filters?.gender}
            options={[
              { label: "Select", value: "" },
              { label: "Male", value: "MALE" },
              { label: "Female", value: "FEMALE" },
              { label: "Others", value: "OTHERS" },
            ]}
            onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
          />
        </div>
      </div>

      {/* Lat/Lng */}
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="mb-1">Latitude:</label>
          <Input
            type="text"
            placeholder="Enter Latitude"
            value={filters?.latitude}
            onChange={(e) => setFilters({ ...filters, latitude: e.target.value })}
          />
        </div>
        <div className="col-md-6">
          <label className="mb-1">Longitude:</label>
          <Input
            type="text"
            placeholder="Enter Longitude"
            value={filters?.longitude}
            onChange={(e) => setFilters({ ...filters, longitude: e.target.value })}
          />
        </div>
      </div>

      {/* Address */}
      <div className="row mb-3">
        <div className="col-12">
          <label className="mb-1">Address / ZIP code:</label>
          <Input
            type="text"
            placeholder="Enter Address"
            value={filters?.address}
            onChange={(e) => setFilters({ ...filters, address: e.target.value })}
          />
        </div>
      </div>

      <div className="d-flex gap-1 float-end">
        <Button text="Apply Filters" callBack={(e) => applyFilters(e)} />
        <Button text="Clear All" callBack={(e) => clearFilters(e)} />
      </div>
    </form>
  );

  return (
    <div>
      {loading && <Loader />}
      <Table
        headings={headings}
        data={users}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        enablePagination={totalElements > pageSize}
        filtersDropdown={filtersDropdown}
        isDropdownFilterOpen={isDropdownFilterOpen}
        setIsDropdownFilterOpen={setIsDropdownFilterOpen}
        card={true}
        pageTitle="User List"
      />

      {/* Modals */}
      <AddUserModal isAddUserOpen={isAddOpen} closeAddUserModal={() => setIsAddOpen(false)} />
      <RemoveUserModal isRemoveOpen={isRemoveOpen} closeRemoveModal={() => setIsRemoveOpen(false)} selectedUserId={selectedUserId} fetchUsers={fetchUsers} />
      <EditUserModal isEditOpen={isEditOpen} closeEditModal={() => setIsEditOpen(false)} selectedUserId={selectedUserId} />
      <UserDetailsModal isViewDetailsOpen={isViewOpen} closeDetailsModal={() => setIsViewOpen(false)} selectedUserId={selectedUserId} />
    </div>
  );
};

export default UserList;
